import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
} from "@mui/material";
import { jwtDecode } from "jwt-decode";
import { patch, post } from "../../../helpers/admin_api_helper";

interface AdminJwtPayload {
  id: number;
  email: string;
  permission: string;
  first_name: string;
  last_name: string;
}

export const AdminProfile: React.FC = () => {
  const [profile, setProfile] = useState<AdminJwtPayload | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [profileSuccess, setProfileSuccess] = useState("");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("admin_access_token");
    if (token) {
      try {
        const decoded = jwtDecode<AdminJwtPayload>(token);
        setProfile(decoded);
        setFirstName(decoded.first_name || "");
        setLastName(decoded.last_name || "");
      } catch {
        // token decode failed
      }
    }
  }, []);

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;
    setProfileError("");
    setProfileSuccess("");
    setProfileLoading(true);
    try {
      await patch(`/admin/${profile.id}`, {
        first_name: firstName,
        last_name: lastName,
      });
      setProfileSuccess("Profile updated successfully.");
    } catch (err: any) {
      setProfileError(
        err?.response?.data?.message || err?.message || "Failed to update profile."
      );
    }
    setProfileLoading(false);
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match.");
      return;
    }

    setPasswordLoading(true);
    try {
      const res = await post("/admin/change-password", {
        currentPassword,
        newPassword,
      });
      if (res?.success) {
        setPasswordSuccess("Password changed successfully.");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        setPasswordError(res?.message || "Failed to change password.");
      }
    } catch (err: any) {
      setPasswordError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to change password."
      );
    }
    setPasswordLoading(false);
  };

  if (!profile) return null;

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", mt: 2 }}>
      {/* Profile Info */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderColor: "#E2E8F0", borderRadius: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "#0B1929" }}>
          My Profile
        </Typography>
        <form onSubmit={handleProfileSave}>
          {profileError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {profileError}
            </Alert>
          )}
          {profileSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {profileSuccess}
            </Alert>
          )}
          <TextField
            label="Email"
            fullWidth
            value={profile.email}
            disabled
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              label="First Name"
              fullWidth
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
            <TextField
              label="Last Name"
              fullWidth
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </Box>
          <Button
            type="submit"
            variant="contained"
            disabled={profileLoading}
          >
            {profileLoading ? "Saving..." : "Save Profile"}
          </Button>
        </form>
      </Paper>

      {/* Change Password */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderColor: "#E2E8F0", borderRadius: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "#0B1929" }}>
          Change Password
        </Typography>
        <form onSubmit={handlePasswordSubmit}>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          {passwordSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {passwordSuccess}
            </Alert>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Current Password"
              type="password"
              required
              fullWidth
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
            <TextField
              label="New Password"
              type="password"
              required
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              helperText="Minimum 8 characters"
            />
            <TextField
              label="Confirm New Password"
              type="password"
              required
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={passwordLoading}
              sx={{ mt: 1 }}
            >
              {passwordLoading ? "Changing..." : "Change Password"}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};
